import React from 'react'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import Link from 'next/link'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { usePaymentStatus } from 'querys/payment'
import { formatToMinutesSeconds } from 'utils/formatTime'
import { Typography } from 'common/typography'
import ContentRenderer from 'content-renderer'
import { IoMdCheckmarkCircle } from 'react-icons/io'
import LearningBadgePath from './learning-badge-path'

const LessonItemStyled = styled.div`
  display: grid;
  grid-template-columns: 8.75rem 1fr;
  grid-template-rows: min-content 1fr;
  grid-template-areas: "thumb title" "description description";
  column-gap: 8px;
  row-gap: 8px;
  box-sizing: border-box;
  cursor: pointer;
  margin: 24px 0;
  position: relative;
  .badge-step {
    position: absolute;
    left: -50px;
  }
  &:last-of-type {
    margin: 24px 0 40px;
  }
  .lesson-number {
    position: absolute;
    background-color: var(--lightBlue);
    width: 20px;
    height: 20px;
    top: 4px;
    left: 4px;
    color: var(--neutral100);
    font-weight: 700;
    text-align: center;
    border-radius: 50%;
    font-size: 12px;
    line-height: 20px;
    z-index: 2;
  }
  .thumbnail-container {
    position: relative;
    .lesson-duration {
      font: var(--smallRegular);
      position: absolute;
      color: var(--white);
      background: var(--neutral100);
      bottom: 4px;
      right: 4px;
      border-radius: 4px;
      padding: 4px 8px;
    }
    .lesson-overlay, .overlay {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .overlay {
    background: var(--neutral100);
    opacity: 0.5;
  }
  .status-icon {
    z-index: 2;
  }
  &:first-of-type {
    margin-top: 16px;
  }
  .lesson-content {
    grid-area: description;
    p {
      display: -webkit-box;
      overflow: hidden;
      font: var(--smallRegular);
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
  }
  .access-overlay {
    opacity: 0;
    transition: 0.3s ease-in;
  }
  .completed {
    display: none;
  }
  .lesson-thumb-container {
    position: relative;
  }
  .lesson-progress {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    block-size: 2px;
    background: var(--neutral60);
  }
  .lesson-progress-bar {
    block-size: 2px;
    background: var(--secondary);
    max-inline-size: ${({ videoProgress }) => `${videoProgress}%` || 0};
  }
  @media(min-width: 768px) {
    padding: 24px;
    grid-template-columns: 280px 1fr;
    column-gap: 16px;
    row-gap: 8px;
    background: var(--neutral100);
    border-radius: 8px;
    &:hover .access-overlay {
      opacity: 1;
    }
    .badge-step {
      left: -126px;
    }
    .lesson-content {
      grid-area: 2 / 2 / 3 / 3;
      p {
        font: var(--regular);
      }
    }
    .thumbnail-container {
      display: flex;
      align-items: center;
      grid-area: 1 / 1 / 3 / 2;
      .lesson-overlay {
        left: 58px;
      }
    }
    .status-icon {
      height: 56px;
    }
    .play-icon {
      height: 54px;
      width: auto;
    }
    .overlay {
      background: var(--neutral110);
      opacity: 0.8;
    }
    .lesson-number {
      display: block;
      min-width: 34px;
      min-height: 32px;
      margin-right: 24px;
      font-size: 24px;
      line-height: 32px;
      background: none;
      position: static;
      color: var(--turquoise);
    }
    .completed {
      display: block;
      height: fit-content;
      grid-area: 1 / 3 / 2 / 4;
    }
    .lesson-progress, .lesson-progress-bar {
      block-size: 4px;
    }
  }
`

function LessonItem({
  slug,
  total_duration,
  has_access,
  is_public,
  title,
  thumbnails,
  numberLesson,
  content: { blocks },
  completed,
  badgeState,
  consecutiveLessonsCompleted,
  progress: courseProgress,
  lessonWidthBadge,
  video,
}) {
  const router = useRouter()
  const { data: { has_access: paymentStatus } } = usePaymentStatus()
  const { query: { course } } = router
  const duration = formatToMinutesSeconds(total_duration)
  const hasAccess = paymentStatus || has_access
  const lessonProgress = video?.progress
  const lessonDuration = video?.duration
  const videoProgress = (lessonProgress / lessonDuration) * 100

  let progress
  if (consecutiveLessonsCompleted <= numberLesson && lessonWidthBadge === numberLesson) {
    progress = courseProgress
  }
  if (consecutiveLessonsCompleted > numberLesson) {
    progress = 100
  }

  const calcAngle = () => {
    if (!progress) return null
    const proporcion = 90 / 25
    const angle = progress * proporcion + 90
    /* TO REPRESENT FROM 0% TO 50% WE MOVE IT FROM 90deg, 180deg, to 270dec the first gradient */
    /* TO REPRESENT FROM 50% TO 100% WE MOVE IT FROM 270deg, 360deg, to 90deg the second gradient */
    // IN THE SECOND GRADIENT 450deg it's similar to 90deg
    return angle
  }

  return (
    <Link href={`/aprender/${course}/${slug}`} passHref>
      <LessonItemStyled completed={completed} videoProgress={videoProgress}>
        <div className="thumbnail-container">
          {
            badgeState?.image && (
              <div className="badge-step">
                <LearningBadgePath
                  src={`${badgeState?.image}`}
                  alt={`Pieza de la medalla que te ganas al completar la clase ${badgeState?.lesson} llamada ${badgeState?.name}`}
                  progress={progress}
                  progressAngle={calcAngle()}
                  completed={completed}
                />
              </div>
            )
          }
          <span className="lesson-number">
            {numberLesson}
          </span>
          <div className="lesson-thumb-container">
            {thumbnails[0]?.link ? (
              <Image
                width={221}
                height={124}
                src={thumbnails[0]?.link}
                alt={`Miniatura del video de la clase de ${title}`}
              />
            ) : null}
            {hasAccess && (
              <div className="lesson-progress">
                <div className="lesson-progress-bar" />
              </div>
            )}
          </div>
          {hasAccess ? (
            <>
              <span className="lesson-duration">{duration}</span>
              <div className="lesson-overlay access-overlay">
                <div className="overlay" />
                <img className="status-icon play-icon" width="29.39" height="28" src="/images/icons/play.png" alt="Icono de clase bloqueada" />
              </div>
            </>
          ) : (
            <div className="lesson-overlay">
              <div className="overlay" />
              <img className="status-icon" width="29.39" height="28" src="/images/courses/landing-padlock.svg" alt="Icono de clase bloqueada" />
            </div>
          )}
        </div>
        <Typography as="h3" small="smallBold" large="headline5">
          {title}
        </Typography>
        <div className="lesson-content">
          <ContentRenderer chunks={blocks.slice(0, 1)} color="var(--textOpaque)" />
        </div>
        {completed && (
          <div className="completed">
            <IoMdCheckmarkCircle size={22} color="var(--turquoise)" />
          </div>
        )}
      </LessonItemStyled>
    </Link>
  )
}

LessonItem.defaultProps = {
}

LessonItem.propTypes = {
}

export default LessonItem
